/**
 * Lightweight singleton store for the floating Video Mini Player.
 *
 * Why not React Context? App.tsx + VideosView.tsx are already large;
 * a singleton keeps changes surgical. Uses useSyncExternalStore so any
 * consumer re-renders cleanly.
 *
 * NOTE: This is intentionally decoupled from the audio MiniPlayer
 * (state in App.tsx). DO NOT touch anything audio-related here.
 */

import { useSyncExternalStore } from 'react';
import type { Video } from './videoService';

export type VideoMiniView = 'hidden' | 'minimized' | 'fullscreen';

export interface MiniRect {
  // px from screen edges; null = use default bottom-right anchor
  bottom: number;
  right: number;
  // mini width in viewport-width percent (40..80)
  widthVw: number;
}

export interface VideoMiniState {
  currentVideo: Video | null;
  view: VideoMiniView;
  currentTime: number;
  isPlaying: boolean;
  miniRect: MiniRect;
}

const DEFAULT_MINI_RECT: MiniRect = {
  bottom: 88, // approx 72px footer + 16 gap
  right: 12,
  widthVw: 58,
};

const initial: VideoMiniState = {
  currentVideo: null,
  view: 'hidden',
  currentTime: 0,
  isPlaying: false,
  miniRect: { ...DEFAULT_MINI_RECT },
};

let state: VideoMiniState = initial;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach(l => l());

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

const getSnapshot = () => state;

export const videoMiniStore = {
  get: () => state,

  open: (video: Video, startTime: number = 0) => {
    state = {
      ...state,
      currentVideo: video,
      view: 'fullscreen',
      currentTime: startTime,
      isPlaying: true,
    };
    notify();
  },

  setView: (view: VideoMiniView) => {
    if (state.view === view) return;
    state = { ...state, view };
    notify();
  },

  setCurrentTime: (t: number) => {
    if (Math.abs((state.currentTime || 0) - t) < 2) return; // throttle: only notify every ~2s
    state = { ...state, currentTime: t };
    notify();
  },

  setPlaying: (p: boolean) => {
    if (state.isPlaying === p) return;
    state = { ...state, isPlaying: p };
    notify();
  },

  setMiniRect: (rect: Partial<MiniRect>) => {
    state = { ...state, miniRect: { ...state.miniRect, ...rect } };
    notify();
  },

  resetMiniRect: () => {
    state = { ...state, miniRect: { ...DEFAULT_MINI_RECT } };
    notify();
  },

  close: () => {
    state = { ...initial, miniRect: { ...DEFAULT_MINI_RECT } };
    notify();
  },
};

export const useVideoMini = (): VideoMiniState =>
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
